import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  List,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Input, Button, Spin } from "antd";
import RoleCountrySettingsSection from "./RoleCountrySettingsSection";

const apiUrl = process.env.REACT_APP_API_Roles;
const countriesManualApi = (process.env.REACT_APP_API_Countries_Manual || process.env.REACT_APP_API_UpdatedCountries || "").trim();
const countriesSpecialApi = (process.env.REACT_APP_API_Countries_Special || process.env.REACT_APP_API_UpdatedCountries || "").trim();
const applicationsApi = process.env.REACT_APP_API_AllApps;

const validateRoleConfig = (roleConfig) => {
  const countrySettings = roleConfig?.country_settings ?? [];
  const batches = roleConfig?.batches ?? [];
  if (!Array.isArray(countrySettings) || countrySettings.length === 0) {
    return "Please add at least one country";
  }
  if (!Array.isArray(batches) || batches.length === 0) {
    return "Please configure at least one batch";
  }
  const batch1 = batches.find((b) => b.batch_number === 1);
  if (!batch1 || batch1.otps_to_unlock_next !== 0) {
    return "Batch 1 must have OTPs to unlock next = 0";
  }
  for (const cs of countrySettings) {
    if (
      (cs.apps_mode === "only_selected" || cs.apps_mode === "except_selected") &&
      (!cs.application_ids || cs.application_ids.length === 0)
    ) {
      return `${cs.country_name || cs.country_id}: select at least one app when using only/except selected`;
    }
  }
  return null;
};

const EditRole = ({ id, setOpen }) => {
  const [title, setTitle] = useState("");
  const [userDb, setUserDb] = useState(null);
  const [roleConfig, setRoleConfig] = useState({ country_settings: [], batches: [] });
  const [countries, setCountries] = useState([]);
  const [applications, setApplications] = useState([]);
  const [errors, setErrors] = useState({
    title: "",
    country_settings: "",
    general: "",
  });
  const [loading, setLoading] = useState(false);
  const [loadingRole, setLoadingRole] = useState(true);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingCountries, setLoadingCountries] = useState(false);

  useEffect(() => {
    const fetchRole = async () => {
      if (!id) return;
      try {
        setLoadingRole(true);
        const response = await axios.get(`${apiUrl}/${id}`);
        const roleData =
          response.data?.data ?? response.data?.body ?? response.data;
        if (roleData?.title) setTitle(roleData.title);
        if (roleData?.user_db) setUserDb(roleData.user_db);
        const csList = roleData?.country_settings ?? [];
        const batchList = roleData?.batches ?? [];
        if (csList.length > 0 || batchList.length > 0) {
          const country_settings = csList.map((cs) => ({
            country_id: cs.country_id,
            country_name: cs.country_name,
            apps_mode: cs.apps_mode || "all",
            application_ids: cs.application_ids || [],
          }));
          const batches = batchList.map((b) => ({
            batch_number: b.batch_number,
            otps_to_unlock_next: b.otps_to_unlock_next ?? 0,
            country_sims: (b.country_sims || []).reduce(
              (acc, { country_id, sims_to_lock }) => {
                acc[country_id] = sims_to_lock ?? 0;
                return acc;
              },
              {}
            ),
          }));
          if (Object.keys(batches[0]?.country_sims || {}).length === 0 && country_settings.length > 0) {
            batches.forEach((b, i) => {
              b.country_sims = Object.fromEntries(
                country_settings.map((cs) => [cs.country_id, b.country_sims?.[cs.country_id] ?? 0])
              );
            });
          }
          setRoleConfig({ country_settings, batches });
        }
      } catch (err) {
        console.error("Error fetching role:", err);
        setErrors((prev) => ({ ...prev, general: "Failed to load role" }));
      } finally {
        setLoadingRole(false);
      }
    };
    fetchRole();
  }, [id]);

  const parseApiBody = (data) => {
    let raw = data?.body ?? data;
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
        return raw?.body ?? raw;
      } catch {
        return [];
      }
    }
    return raw;
  };

  useEffect(() => {
    const fetchApps = async () => {
      setLoadingData(true);
      try {
        const appsRes = await axios.get(applicationsApi);
        const appsBody = parseApiBody(appsRes?.data);
        setApplications(Array.isArray(appsBody) ? appsBody : []);
      } catch (err) {
        console.error("Error fetching applications:", err);
      } finally {
        setLoadingData(false);
      }
    };
    fetchApps();
  }, []);

  useEffect(() => {
    if (!id || loadingRole) return;
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const api = (userDb === "special" ? countriesSpecialApi : countriesManualApi).trim();
        const res = await axios.get(api);
        const data = res?.data;
        let arr = data?.body ?? data;
        if (typeof arr === "string") {
          try {
            arr = JSON.parse(arr);
            arr = arr?.body ?? arr;
          } catch {
            arr = [];
          }
        }
        setCountries(Array.isArray(arr) ? arr : []);
      } catch (err) {
        console.error("Error fetching countries:", err);
        setCountries([]);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, [id, loadingRole, userDb]);

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    setErrors((prev) => ({ ...prev, title: "" }));
  };

  const buildPayload = () => {
    const country_settings = (roleConfig?.country_settings ?? []).map((cs) => ({
      country_id: cs.country_id,
      apps_mode: cs.apps_mode || "all",
      application_ids: cs.application_ids || [],
    }));
    const batches = (roleConfig?.batches ?? []).map((b) => ({
      batch_number: b.batch_number,
      otps_to_unlock_next: b.otps_to_unlock_next ?? 0,
      country_sims: Object.entries(b.country_sims || {}).map(([country_id, sims_to_lock]) => ({
        country_id: Number(country_id),
        sims_to_lock: sims_to_lock ?? 0,
      })),
    }));
    return { country_settings, batches };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = { title: "", country_settings: "", general: "" };

    if (!title.trim()) {
      newErrors.title = "Please enter the role title";
    }

    const csError = validateRoleConfig(roleConfig);
    if (csError) {
      newErrors.country_settings = csError;
    }

    if (newErrors.title || newErrors.country_settings) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);
      const { country_settings, batches } = buildPayload();
      const payload = {
        title: title.trim(),
        is_tester: 1,
        country_settings,
        batches,
      };
      const response = await axios.put(`${apiUrl}/${id}`, payload);
      if (response.status === 200) {
        setOpen(false);
      } else {
        throw new Error("Failed to edit role");
      }
    } catch (error) {
      console.error("Error editing role:", error);
      setErrors({
        ...errors,
        general: error.response?.data?.errorMsg || "Failed to edit role",
      });
    } finally {
      setLoading(false);
    }
  };

  const isLoading = loadingRole || loadingData || loadingCountries;

  return (
    <>
      <AppBar position="relative">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flex: 1 }}>
            Edit Role
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => setOpen(false)}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <List style={{ height: "100vh" }}>
        <Grid
          container
          style={{ height: "100%", justifyContent: "center" }}
          alignItems="flex-start"
        >
          <Grid item xs={12} sm={10} md={8} lg={8}>
            {loadingRole ? (
              <div style={{ padding: 48, textAlign: "center" }}>
                <Spin size="large" />
                <p style={{ marginTop: 16, color: "#64748b" }}>
                  Loading role...
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="mx-10"
                style={{ paddingBottom: 24 }}
              >
                <div className="mb-4">
                  <h2 className="text-2xl font-bold">Edit Role</h2>
                </div>
                <div className="grid grid-cols-1 gap-4 mb-4">
                  <Input
                    value={title}
                    onChange={handleTitleChange}
                    placeholder="Enter Role Title"
                    addonBefore="Role Title"
                    className="mb-4"
                  />
                  {userDb != null && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-2">
                        User Database
                      </label>
                      <Input
                        value={userDb === "manual" ? "Manual" : userDb === "special" ? "Special" : userDb || "—"}
                        disabled
                        addonBefore="Database"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Cannot be changed after role creation.
                      </p>
                    </div>
                  )}
                </div>
                {errors.title && (
                  <p className="text-red-500 text-xs">{errors.title}</p>
                )}

                {loadingData ? (
                  <p style={{ color: "#64748b" }}>
                    Loading countries and applications...
                  </p>
                ) : (
                  <RoleCountrySettingsSection
                    countries={countries}
                    applications={applications}
                    value={roleConfig}
                    onChange={setRoleConfig}
                    errors={errors}
                  />
                )}

                {errors.general && (
                  <p className="text-red-500 text-xs mt-2">{errors.general}</p>
                )}
                <div className="flex justify-end" style={{ marginTop: 24 }}>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Edit Role
                  </Button>
                </div>
              </form>
            )}
          </Grid>
        </Grid>
      </List>
    </>
  );
};

export default EditRole;
